import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { LANGUAGES, CEFR_LEVELS } from '@/lib/languages'
import { analytics } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import type { CEFRLevel } from '@/types/database'

export function Onboarding() {
  const navigate = useNavigate()
  const { updateProfile } = useAuth()
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [nativeLanguage, setNativeLanguage] = useState('')
  const [targetLanguage, setTargetLanguage] = useState('')
  const [level, setLevel] = useState<CEFRLevel>('A1')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleComplete = async () => {
    if (!name.trim() || !nativeLanguage || !targetLanguage || !level) {
      setError('Please complete all fields')
      return
    }

    if (nativeLanguage === targetLanguage) {
      setError('Native and target language must be different')
      return
    }

    setIsLoading(true)
    setError(null)

    const { error } = await updateProfile({
      name: name.trim(),
      native_language: nativeLanguage,
      target_language: targetLanguage,
      level,
    })

    if (error) {
      setError(error.message)
      setIsLoading(false)
      return
    }

    analytics.onboardingStepCompleted(4, 'level')
    analytics.onboardingCompleted(nativeLanguage, targetLanguage, level)
    navigate('/')
  }

  const availableTargetLanguages = LANGUAGES.filter(l => l.code !== nativeLanguage)

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome to AI Language Tutor</CardTitle>
          <CardDescription>
            Let's set up your learning preferences
          </CardDescription>
          <div className="flex justify-center gap-2 pt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 w-10 rounded-full ${
                  s <= step ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">What's your name?</Label>
                <Input
                  id="name"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && name.trim()) {
                      analytics.onboardingStepCompleted(1, 'name')
                      setStep(2)
                    }
                  }}
                  maxLength={100}
                  autoFocus
                />
                <p className="text-sm text-muted-foreground">
                  Your tutor will use this to address you
                </p>
              </div>
              <Button
                className="w-full"
                onClick={() => {
                  analytics.onboardingStepCompleted(1, 'name')
                  setStep(2)
                }}
                disabled={!name.trim()}
              >
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>What is your native language?</Label>
                <SearchableSelect
                  value={nativeLanguage}
                  onValueChange={(value) => {
                    setNativeLanguage(value)
                    setTimeout(() => {
                      analytics.onboardingStepCompleted(2, 'native_language')
                      setStep(3)
                    }, 150)
                  }}
                  options={LANGUAGES.map((lang) => ({
                    value: lang.code,
                    label: `${lang.name} (${lang.nativeName})`,
                    searchTerms: [lang.name, lang.nativeName],
                  }))}
                  placeholder="Select your native language"
                  searchPlaceholder="Search languages..."
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    analytics.onboardingStepCompleted(2, 'native_language')
                    setStep(3)
                  }}
                  disabled={!nativeLanguage}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Which language do you want to learn?</Label>
                <SearchableSelect
                  value={targetLanguage}
                  onValueChange={(value) => {
                    setTargetLanguage(value)
                    setTimeout(() => {
                      analytics.onboardingStepCompleted(3, 'target_language')
                      setStep(4)
                    }, 150)
                  }}
                  options={availableTargetLanguages.map((lang) => ({
                    value: lang.code,
                    label: `${lang.name} (${lang.nativeName})`,
                    searchTerms: [lang.name, lang.nativeName],
                  }))}
                  placeholder="Select target language"
                  searchPlaceholder="Search languages..."
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    analytics.onboardingStepCompleted(3, 'target_language')
                    setStep(4)
                  }}
                  disabled={!targetLanguage}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div
              className="space-y-4"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleComplete()
                }
              }}
            >
              <div className="space-y-2">
                <Label>What is your current level?</Label>
                <p className="text-sm text-muted-foreground">
                  Don't worry, our AI will adapt to your actual level as you practice
                </p>
              </div>
              <div className="grid gap-2">
                {CEFR_LEVELS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLevel(l.code as CEFRLevel)}
                    className={`p-4 text-left rounded-lg border transition-colors ${
                      level === l.code
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-medium">
                      {l.code === 'A0' ? l.name : `${l.code} - ${l.name}`}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {l.description}
                    </div>
                  </button>
                ))}
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(3)}>
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleComplete}
                  disabled={isLoading}
                >
                  {isLoading ? 'Setting up...' : 'Start Learning'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
